import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/pages/About";
import { CtaBand } from "@/pages/Home";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { developments as fallbackDevelopments } from "@/data/developments";
import { useDevelopments } from "@/hooks/useDevelopments";
import { fmtPrice, fmtRange } from "@/utils/money";

export default function Developments() {
  const { t } = useLanguage();
  const { developments } = useDevelopments(fallbackDevelopments);
  const [area, setArea] = useState("all");
  const [query, setQuery] = useState("");

  const areas = ["all", ...new Set(developments.map((d) => d.area))];
  const q = query.trim().toLowerCase();
  const filtered = developments
    .filter((d) => (area === "all" ? true : d.area === area))
    .filter((d) => (!q ? true : `${d.name} ${d.area} ${d.address}`.toLowerCase().includes(q)));

  return (
    <main data-testid="developments-page">
      <Seo
        title="The Next Miami — Pre-Construction & New Construction | Carlos Vindel Realty"
        siteName="Carlos Vindel Realty"
        description="A curated look at South Florida's most anticipated pre-construction projects: Aston Martin Residences, Bentley Residences, St. Regis, Anantara and more. Early-buyer guidance with Carlos Vindel."
      />
      <PageHero testid="developments-hero" overline={t.developmentsPage.overline} title={t.developmentsPage.title} subtitle={t.developmentsPage.subtitle} />

      <section className="bg-cream py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-8 flex items-center bg-white border border-sand rounded-full p-1.5 pl-6 shadow-[0_4px_24px_rgba(13,19,26,0.06)] max-w-2xl">
            <Search className="w-4 h-4 text-gold shrink-0" />
            <input
              data-testid="developments-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.developmentsPage.searchPlaceholder}
              className="flex-1 min-w-0 bg-transparent text-ink text-sm placeholder:text-[#8C96A4] focus:outline-none py-2.5 px-3"
            />
          </div>

          <div data-testid="developments-area-filters" className="flex flex-wrap gap-2 mb-12">
            {areas.map((a) => (
              <button
                key={a}
                data-testid={`area-filter-${a.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setArea(a)}
                className={`text-xs uppercase tracking-[0.2em] font-semibold px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  area === a ? "bg-ink text-cream border-ink" : "bg-transparent text-[#525D6B] border-sand hover:border-gold hover:text-gold-dark"
                }`}
              >
                {a === "all" ? t.developmentsPage.allAreas : a}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((d, i) => (
                <motion.div key={d.id} layout initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
                  <Link
                    to={`/contact?development=${encodeURIComponent(d.name)}`}
                    data-testid={`developments-page-card-${d.id}`}
                    className="group relative block aspect-[16/11] overflow-hidden bg-ink"
                  >
                    <img
                      src={d.image}
                      alt={`${d.name}, ${d.area}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                    <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.25em] font-semibold bg-gold text-ink px-3 py-1.5">
                      {d.area}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <p className="font-serif text-cream text-2xl leading-tight">{d.name}</p>
                      <p className="mt-1.5 text-cream/60 text-xs">{d.address}</p>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-gold font-serif text-lg">
                          {t.developments.from} {fmtPrice(d.priceFrom)}
                          <span className="text-cream/40 text-xs font-sans ml-2">{fmtRange(d.priceFrom, d.priceTo)}</span>
                        </p>
                        <span className="flex items-center gap-1.5 text-cream/70 text-xs font-semibold uppercase tracking-[0.15em] opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                          {t.developments.cta} <ArrowRight className="w-3.5 h-3.5 text-gold" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <p data-testid="developments-empty" className="text-center text-[#8C96A4] py-16 font-serif italic text-xl">
              {t.listingsPage.empty}
            </p>
          )}

          <Reveal className="mt-14 text-center">
            <p className="text-[#525D6B] text-sm max-w-xl mx-auto leading-relaxed">{t.developmentsPage.note}</p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
